import { useState } from "react";
import { HandCoins, Truck, Check } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import { useApp } from "@/context/AppContext";
import { fmtDate, uuid, vals } from "@/lib/format";
import { BigButton, Chip } from "@/components/ui-kit";
import InvoiceGallery from "@/components/purchases/InvoiceGallery";
import { NumPad, AmountDisplay } from "@/components/NumPad";

function PayForm({ supplier, onDone }) {
  const { t, money, dispatch } = useApp();
  const [amount, setAmount] = useState("");
  const owed = Math.max(supplier.balance || 0, 0);
  const save = () => {
    if (dispatch("supplier_payment.create", { id: uuid(), supplier_id: supplier.id, supplier_name: supplier.name, amount: Number(amount) })) {
      toast.success(t("supplier_payment_saved"));
      onDone();
    }
  };
  return (
    <div className="space-y-3" data-testid="supplier-pay-form">
      <AmountDisplay value={amount} label={t("amount")} testid="supplier-pay-amount-display" />
      {owed > 0 && <button type="button" onClick={() => setAmount(String(owed))} className="w-full h-11 rounded-xl border-2 border-[#1F9D55] text-[#1F9D55] font-bold" data-testid="supplier-pay-full-btn">{t("pay_all")} · {money(owed)}</button>}
      <NumPad value={amount} onChange={setAmount} testid="supplier-pay-numpad" />
      <BigButton variant="green" disabled={!(Number(amount) > 0)} onClick={save} className="w-full" data-testid="supplier-pay-confirm-btn"><Check className="w-5 h-5" />{t("confirm")}</BigButton>
    </div>
  );
}

export default function SupplierDetail({ supplierId, onClose }) {
  const { view, t, money, lang } = useApp();
  const [paying, setPaying] = useState(false);
  const [tab, setTab] = useState("history");
  const s = supplierId ? view.suppliers[supplierId] : null;
  const close = () => { setPaying(false); onClose(); };
  if (!s) return null;
  const history = [
    ...vals(view.purchases).filter((p) => p.supplier_id === s.id).map((p) => ({ id: p.id, at: p.created_at, kind: "buy", amount: p.total, owed: p.total - (p.paid ?? p.total), pending: p.pending })),
    ...vals(view.supplier_payments).filter((p) => p.supplier_id === s.id).map((p) => ({ id: p.id, at: p.created_at, kind: "pay", amount: p.amount, pending: p.pending })),
  ].sort((a, b) => b.at.localeCompare(a.at));

  return (
    <Dialog open={!!s} onOpenChange={(o) => !o && close()}>
      <DialogContent className="max-w-md max-h-[94vh] overflow-y-auto" data-testid="supplier-detail-dialog">
        <DialogTitle className="font-heading text-2xl">{s.name}</DialogTitle>
        <div className="rounded-2xl bg-secondary p-4">
          <div className="text-sm font-bold text-muted-foreground">{t("we_owe")}</div>
          <div className={`text-4xl font-extrabold ${s.balance > 0 ? "text-destructive" : "text-[#1F9D55]"}`} data-testid="supplier-detail-balance">{money(s.balance || 0)}</div>
        </div>
        {paying ? <PayForm supplier={s} onDone={() => setPaying(false)} /> : (
          <BigButton variant="green" onClick={() => setPaying(true)} className="w-full" data-testid="supplier-pay-btn"><HandCoins className="w-5 h-5" />{t("pay_supplier")}</BigButton>
        )}
        <div className="flex gap-2">
          <Chip active={tab === "history"} onClick={() => setTab("history")} data-testid="supplier-tab-history">{t("history")}</Chip>
          <Chip active={tab === "invoices"} onClick={() => setTab("invoices")} data-testid="supplier-tab-invoices">{t("invoices")}</Chip>
        </div>
        {tab === "invoices" ? <InvoiceGallery supplierId={s.id} /> : (<>
        {history.length === 0 && <p className="text-muted-foreground">{t("no_history")}</p>}
        <div className="space-y-2" data-testid="supplier-history-list">
          {history.map((h) => (
            <div key={h.id} className="rounded-xl bg-white border border-border p-3 flex items-center gap-3">
              <span className={`w-10 h-10 rounded-full grid place-items-center ${h.kind === "pay" ? "bg-[#E3F6EC] text-[#1F9D55]" : "bg-[#F39C12]/15 text-[#B9770E]"}`}>
                {h.kind === "pay" ? <HandCoins className="w-5 h-5" /> : <Truck className="w-5 h-5" />}
              </span>
              <div className="flex-1 min-w-0">
                <div className="font-bold">{h.kind === "pay" ? t("supplier_payment") : t("purchase")}</div>
                <div className="text-xs text-muted-foreground">{fmtDate(h.at, lang)}{h.pending && ` · ${t("pending")}`}</div>
              </div>
              <div className={`font-extrabold text-end ${h.kind === "pay" ? "text-[#1F9D55]" : ""}`}>
                {h.kind === "pay" ? `-${money(h.amount)}` : money(h.amount)}
                {h.kind === "buy" && h.owed > 0 && <div className="text-xs text-destructive">+{money(h.owed)}</div>}
              </div>
            </div>
          ))}
        </div>
        </>)}
      </DialogContent>
    </Dialog>
  );
}
